import { Button } from "@/components/ui/button.tsx";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card.tsx";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible.tsx";
import { m } from "@/paraglide/messages.js";
import type { ErrorComponentProps } from "@tanstack/react-router";
import {
	AlertTriangle,
	ChevronDown,
	ChevronUp,
	MessageSquare,
	RefreshCcw,
	Sparkles,
	Undo2,
} from "lucide-react";
import { lazy, Suspense, useState } from "react";

// Lazy because these two components are values passed to createRouter as
// `defaultErrorComponent` / `defaultNotFoundComponent`, so the router plugin
// cannot code-split them — they sit in the ENTRY chunk, paid on /auth/login. The
// support dialog is behind a useState toggle and pulls ui/dialog.tsx, which
// nothing else in the entry closure needs.
const SupportDialog = lazy(() =>
	import("@/components/support-dialog").then((module) => ({
		default: module.SupportDialog,
	})),
);

function isChunkLoadError(error: unknown): boolean {
	if (!(error instanceof Error)) {
		return false;
	}
	const msg = error.message ?? "";
	return (
		msg.includes("Failed to fetch dynamically imported module") ||
		msg.includes("Importing a module script failed") ||
		msg.includes("Failed to load module script") ||
		error.name === "ChunkLoadError"
	);
}

function ChunkErrorCard() {
	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<Card className="w-full max-w-md shadow-lg">
				<CardHeader className="text-center space-y-3 pb-6">
					<div className="flex justify-center">
						<div className="rounded-full bg-primary/10 p-4">
							<Sparkles className="h-10 w-10 text-primary" />
						</div>
					</div>
					<CardTitle className="text-2xl font-semibold">
						{m.error_chunk_title()}
					</CardTitle>
					<CardDescription className="text-base">
						{m.error_chunk_description()}
					</CardDescription>
				</CardHeader>
				<CardFooter className="pt-2">
					<Button
						onClick={() => {
							window.location.reload();
						}}
						variant="default"
						className="w-full gap-2"
					>
						<RefreshCcw className="h-4 w-4" />
						{m.error_chunk_reload()}
					</Button>
				</CardFooter>
			</Card>
		</div>
	);
}

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
	const [showDetails, setShowDetails] = useState(false);
	const [supportOpen, setSupportOpen] = useState(false);

	console.error(error);

	if (isChunkLoadError(error)) {
		return <ChunkErrorCard />;
	}

	// Extract error message and stack
	const errorMessage =
		error instanceof Error ? error.message : String(error || "Unknown error");
	const errorStack = error instanceof Error ? error.stack : undefined;

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<Card className="w-full max-w-2xl shadow-lg">
				<CardHeader className="text-center space-y-3 pb-6">
					<div className="flex justify-center">
						<div className="rounded-full bg-destructive/10 p-4">
							<AlertTriangle className="h-12 w-12 text-destructive" />
						</div>
					</div>
					<CardTitle className="text-3xl font-semibold">
						{m.error_title()}
					</CardTitle>
					<CardDescription className="text-base">
						{m.error_subtitle()}
					</CardDescription>
				</CardHeader>

				<CardContent className="space-y-6">
					<div className="bg-muted/50 rounded-lg p-4 space-y-2">
						<p className="text-sm text-muted-foreground">
							{m.error_description()}
						</p>
						<p className="text-sm font-medium text-foreground">
							{m.error_help_text()}
						</p>
					</div>

					<Collapsible open={showDetails} onOpenChange={setShowDetails}>
						<CollapsibleTrigger
							render={
								<Button
									variant="outline"
									size="sm"
									className="w-full justify-between"
								/>
							}
						>
							<span className="flex items-center gap-2">
								<AlertTriangle className="h-4 w-4" />
								{showDetails ? m.error_hide_details() : m.error_show_details()}
							</span>
							{showDetails ? (
								<ChevronUp className="h-4 w-4" />
							) : (
								<ChevronDown className="h-4 w-4" />
							)}
						</CollapsibleTrigger>
						<CollapsibleContent className="mt-4">
							<div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 space-y-2">
								<p className="font-mono text-xs font-semibold text-destructive">
									{m.error_technical_details()}
								</p>
								<div className="bg-background rounded p-3 overflow-auto max-h-60">
									<pre className="text-xs text-muted-foreground whitespace-pre-wrap wrap-break-word">
										{import.meta.env.PROD
											? "An unexpected error occurred. Please try again."
											: `${errorMessage}\n\n${errorStack || ""}`}
									</pre>
								</div>
							</div>
						</CollapsibleContent>
					</Collapsible>
				</CardContent>

				<CardFooter className="flex flex-col gap-3 pt-6">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
						<Button
							onClick={() => {
								window.location.reload();
							}}
							variant="default"
							className="w-full gap-2"
						>
							<RefreshCcw className="h-4 w-4" />
							{m.error_actions_try_again()}
						</Button>

						<Button
							variant="outline"
							className="w-full gap-2"
							onClick={() => {
								window.history.back();
							}}
						>
							<Undo2 className="h-4 w-4" />
							{m.error_actions_go_back()}
						</Button>
					</div>

					<Button
						onClick={() => setSupportOpen(true)}
						variant="secondary"
						className="w-full gap-2"
					>
						<MessageSquare className="h-4 w-4" />
						{m.error_actions_report_issue()}
					</Button>
				</CardFooter>
			</Card>

			{supportOpen && (
				<Suspense fallback={null}>
					<SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />
				</Suspense>
			)}
		</div>
	);
}
